/**
 * La cotización que llega a Rev. Com. tiene que conservar SKUs y % de descuento.
 * Un guardado con la pantalla vieja (o un 0 de Flexible/Commit) no puede borrar
 * una línea o un porcentaje que ya estaba en la propuesta vigente.
 */

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const optionLines = (opt) => (Array.isArray(opt?.licencias) ? opt.licencias : []);

const scoreOption = (opt) => ({
  count: optionLines(opt).length,
  discount: optionLines(opt).reduce((acc, line) => acc + num(line?.descuento), 0),
  services: Array.isArray(opt?.serviciosAdicionales) ? opt.serviciosAdicionales.length : 0
});

const proposalOptions = (proposal) => {
  if (Array.isArray(proposal?.opciones) && proposal.opciones.length > 0) return proposal.opciones;
  if (!proposal) return [];
  return [{
    licencias: proposal.licencias,
    serviciosAdicionales: proposal.serviciosAdicionales,
    isRecommended: true
  }];
};

const scoreProposal = (proposal) => {
  const opts = proposalOptions(proposal);
  const active = opts.find((opt) => opt?.isRecommended) || opts[0] || {};
  const activeScore = scoreOption(active);
  const all = opts.reduce((acc, opt) => {
    const score = scoreOption(opt);
    acc.count += score.count;
    acc.discount += score.discount;
    return acc;
  }, { count: 0, discount: 0 });
  return { ...activeScore, allCount: all.count, allDiscount: all.discount };
};

const isRicher = (left, right) => {
  if (left.count !== right.count) return left.count > right.count;
  if (left.discount !== right.discount) return left.discount > right.discount;
  if (left.services !== right.services) return left.services > right.services;
  if (left.allCount !== right.allCount) return left.allCount > right.allCount;
  if (left.allDiscount !== right.allDiscount) return left.allDiscount > right.allDiscount;
  return false;
};

/** Entre la propuesta del lead y la que trae la pantalla, se queda la más completa. */
export const preferCompleteProposal = (primary, secondary) => {
  if (!primary) return secondary || null;
  if (!secondary) return primary;
  return isRicher(scoreProposal(secondary), scoreProposal(primary)) ? secondary : primary;
};

const lineKey = (line, kind) => {
  if (!line) return '';
  if (kind === 'service') return String(line.id || line.name || '');
  return String(line.sku || '');
};

const discountChanged = (before, after) => num(before) !== num(after);

/**
 * % que debe quedar en la línea.
 * Un 0 no borra un descuento que la pantalla no estaba mostrando.
 * Si el ejecutivo sí vio el % y lo cambió, gana lo que acaba de capturar.
 */
export const rebaseDiscount = (liveLine, baseLine, editedLine, field = 'descuento') => {
  const live = num(liveLine?.[field]);
  const edit = num(editedLine?.[field]);
  const base = baseLine ? num(baseLine[field]) : null;
  const touched = Boolean(baseLine) && discountChanged(baseLine[field], editedLine?.[field]);
  if (!liveLine) return editedLine?.[field] ?? 0;
  // Solo pisa el % vigente si la pantalla partió de ese mismo número.
  if (touched && base === live) return editedLine[field];
  if (live > 0) return liveLine[field];
  if (edit > 0) return editedLine[field];
  if (liveLine?.[field] == null && editedLine?.[field] == null) {
    return field === 'descuento' ? 0 : undefined;
  }
  return liveLine[field] != null ? liveLine[field] : (editedLine?.[field] ?? 0);
};

const rebaseQty = (liveLine, baseLine, editedLine) => {
  const touched = Boolean(baseLine) && num(baseLine.qty) !== num(editedLine?.qty);
  if (touched) return editedLine.qty;
  if (liveLine?.qty != null) return liveLine.qty;
  return editedLine?.qty;
};

const rebaseLines = (live = [], base = [], edited = [], kind = 'sku') => {
  const keyOf = (line) => lineKey(line, kind);
  const baseBy = new Map((base || []).filter((line) => keyOf(line)).map((line) => [keyOf(line), line]));
  const liveBy = new Map((live || []).filter((line) => keyOf(line)).map((line) => [keyOf(line), line]));
  const out = [];
  const seen = new Set();

  for (const line of edited || []) {
    const key = keyOf(line);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const previous = baseBy.get(key);
    const current = liveBy.get(key);
    if (!current) {
      out.push(line);
      continue;
    }
    const next = {
      ...current,
      ...line,
      qty: rebaseQty(current, previous, line),
      descuento: rebaseDiscount(current, previous, line)
    };
    const google = rebaseDiscount(current, previous, line, 'descuentoGoogle');
    if (google == null) delete next.descuentoGoogle;
    else next.descuentoGoogle = google;
    out.push(next);
  }

  for (const line of live || []) {
    const key = keyOf(line);
    if (!key || seen.has(key)) continue;
    if (baseBy.has(key)) continue;
    out.push(line);
  }
  return out;
};

const sameValue = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const pickEdited = (liveValue, baseValue, editedValue) => (
  sameValue(baseValue, editedValue) ? (liveValue != null ? liveValue : editedValue) : editedValue
);

/** Firestore rechaza `undefined`. Si el campo nunca existió, no se escribe. */
const definedFields = (fields) => {
  const out = {};
  Object.keys(fields).forEach((key) => {
    if (fields[key] !== undefined) out[key] = fields[key];
  });
  return out;
};

const rebaseOptions = (latest, baseline, edited) => {
  const liveOps = proposalOptions(latest);
  const baseOps = proposalOptions(baseline);
  const editOps = proposalOptions(edited);
  const liveBy = new Map(liveOps.map((opt, index) => [String(opt?.id ?? index), opt]));
  const baseBy = new Map(baseOps.map((opt, index) => [String(opt?.id ?? index), opt]));
  const seen = new Set();
  const opciones = [];

  editOps.forEach((opt, index) => {
    const key = String(opt?.id ?? index);
    seen.add(key);
    const live = liveBy.get(key) || liveOps[index];
    const base = baseBy.get(key) || baseOps[index];
    if (!live) {
      opciones.push(opt);
      return;
    }
    opciones.push({
      ...live,
      ...opt,
      ...definedFields({
        titulo: pickEdited(live.titulo, base?.titulo, opt.titulo),
        vigenciaLabel: pickEdited(live.vigenciaLabel, base?.vigenciaLabel, opt.vigenciaLabel),
        billingFrequency: pickEdited(live.billingFrequency, base?.billingFrequency, opt.billingFrequency),
        isRecommended: pickEdited(live.isRecommended, base?.isRecommended, opt.isRecommended)
      }),
      licencias: rebaseLines(live.licencias, base?.licencias, opt.licencias, 'sku'),
      serviciosAdicionales: rebaseLines(
        live.serviciosAdicionales,
        base?.serviciosAdicionales,
        opt.serviciosAdicionales,
        'service'
      )
    });
  });

  liveOps.forEach((opt, index) => {
    const key = String(opt?.id ?? index);
    if (seen.has(key)) return;
    if (baseBy.has(key)) return;
    opciones.push(opt);
  });
  return opciones;
};

/**
 * Escribe la vigencia que el ejecutivo eligió en Rev. Comercial
 * sobre la opción activa. Si no, al confirmar se restaura el plazo de la cotización.
 */
export const mergeVigenciaIntoProposal = (proposal, vigenciaLabel) => {
  const label = String(vigenciaLabel || '').trim();
  if (!proposal || !label) return proposal || null;
  const next = JSON.parse(JSON.stringify(proposal));
  next.vigenciaLabel = label;
  if (next.anexo && typeof next.anexo === 'object') {
    next.anexo = { ...next.anexo, vigenciaLabel: label };
  }
  if (Array.isArray(next.opciones) && next.opciones.length > 0) {
    const activeIdx = next.opciones.findIndex((opt) => opt?.isRecommended);
    const i = activeIdx >= 0 ? activeIdx : 0;
    next.opciones[i] = { ...next.opciones[i], vigenciaLabel: label };
  }
  return next;
};

/**
 * Aplica solo lo que cambió en el editor sobre la propuesta vigente.
 * Así una pantalla abierta con la cotización vieja no pisa SKUs ni descuentos nuevos.
 */
export const rebaseProposalEdits = (latest, baseline, edited) => {
  if (!edited) return latest || null;
  if (!latest) return edited;
  const liveHasLines = proposalOptions(latest).some((opt) => optionLines(opt).length > 0);
  if (!liveHasLines) return edited;

  const opciones = rebaseOptions(latest, baseline, edited);
  const active = opciones.find((opt) => opt?.isRecommended) || opciones[0] || {};
  return {
    ...latest,
    ...edited,
    ...definedFields({
      vigenciaLabel: pickEdited(latest.vigenciaLabel, baseline?.vigenciaLabel, edited.vigenciaLabel) || active.vigenciaLabel,
      billingFrequency: pickEdited(latest.billingFrequency, baseline?.billingFrequency, edited.billingFrequency) || active.billingFrequency,
      currency: pickEdited(latest.currency, baseline?.currency, edited.currency),
      includeIva: pickEdited(latest.includeIva, baseline?.includeIva, edited.includeIva)
    }),
    opciones,
    licencias: active.licencias || edited.licencias || latest.licencias
  };
};

const withoutGoogleDiscount = (lines) => {
  if (!Array.isArray(lines)) return lines;
  return lines.map((line) => {
    if (!line || line.descuentoGoogle == null) return line;
    const next = { ...line };
    delete next.descuentoGoogle;
    return next;
  });
};

/** La propuesta que ve el cliente no lleva el descuento de Google. */
export const proposalForClient = (proposal) => {
  if (!proposal || typeof proposal !== 'object') return proposal;
  const next = { ...proposal };
  if (Array.isArray(next.licencias)) next.licencias = withoutGoogleDiscount(next.licencias);
  if (Array.isArray(next.opciones)) {
    next.opciones = next.opciones.map((opt) => (
      opt ? { ...opt, licencias: withoutGoogleDiscount(opt.licencias) } : opt
    ));
  }
  return next;
};

/** Pesos que la empresa conserva: (Google % − al cliente %) del precio de lista, por un mes. */
export function marginSpreadAmount(listPrice, qty, googlePct, clientPct) {
  const spread = Math.max(0, (Number(googlePct) || 0) - (Number(clientPct) || 0));
  const amount = (Number(listPrice) || 0) * (spread / 100) * (Number(qty) || 0);
  return Math.round(amount * 100) / 100;
}

/** Lo que se le paga al ejecutivo: 10% de ese margen conservado. */
export function marginProtectionBonus(listPrice, qty, googlePct, clientPct) {
  return Math.round(marginSpreadAmount(listPrice, qty, googlePct, clientPct) * 0.1 * 100) / 100;
}

export const leadForClient = (lead) => {
  if (!lead) return lead;
  return {
    ...lead,
    proposal: proposalForClient(lead.proposal),
    licencias: withoutGoogleDiscount(lead.licencias)
  };
};
